import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ExclamationCircleFilled, CloseOutlined } from '@ant-design/icons';
import { Spin, Modal } from 'antd';
import qs from 'query-string';

import LeaveAlert from '~components/leave-alert';
import PropertyDetail from '~pages/pms-dashboard/properties/listing-management/property-detail';
import NavigationBar, {
  showNavigationLabels,
} from '~pages/pms-dashboard/properties/listing-management/navigation-bar';
import AddressDetail from '~pages/pms-dashboard/properties/listing-management/address-detail';
import AcceptanceCriteria from '~pages/pms-dashboard/properties/listing-management/acceptance-criteria';
import FacilityDetail from '~pages/pms-dashboard/properties/listing-management/facility-detail';
import RoomDetail from '~pages/pms-dashboard/properties/listing-management/room-detail';
import ListingDetail from '~pages/pms-dashboard/properties/listing-management/listing-detail';
import PaymentPlan from '~pages/pms-dashboard/properties/listing-management/payment-plan';
import PaymentMethod from '~pages/pms-dashboard/properties/listing-management/payment-method';
import GalleryManagement from '~pages/pms-dashboard/properties/listing-management/gallery-management';
import SubmitSuccess from '~pages/pms-dashboard/properties/listing-management/pending-approval/submit-success';
import Reminder from '~pages/pms-dashboard/properties/listing-management/pending-approval/reminder';
import { PriceCalcDisplay } from '~pages/pms-dashboard/properties/listing-management/price-calc-display';
import Header from '~components/property-header';
import generatePath from '~settings/routing';
import { communicationStatus, platformEntity, entityAction } from '~constants';
import { propertySections, propertyStatus } from '~constants/listing-management';
import { propertyDraftStatus, propertyDraftCategory } from '~constants/pending-approval';
import * as actions from '~actions/pms-actions/properties/listing-management';
import * as pendingApprovalActions from '~actions/pms-actions/pending-approval';
import { isEmptyObject } from '~helpers/property-edit';
import { fireCustomEvent } from '~helpers/custom-events';
import authControl from '~components/auth-control';
import PendingAlertTips from '~components/pending-alert-tips';
import { trackViewPropertyManagePage } from '../track';
import { bookingJourneyType } from '~client/constants';

const mapStateToProps = state => {
  const data = state.pmsReducer.dashboard.listingManagement.toJS();
  const auth = state.auth.toJS();
  return {
    publish: data.communication.publish.status,
    communication: data.communication,
    property: data.payload,
    copiedRoomListings: data.copiedRoomListings,
    galleryProperty: data.galleryProperty,
    allowNoPayment: auth.base.allowNoPayment,
  };
};

const mapDispatchToProps = dispatch => ({
  getPropertyDetail: (slug, successCallback) => {
    dispatch(actions.getPropertyDetail(slug, successCallback));
  },
  getPropertyUnitTypeFacilities: successCallback => {
    dispatch(actions.getUnitTypeFacilities(successCallback));
  },
  setProperty: property => {
    dispatch(actions.setProperty(property));
  },
  setGalleryProperty: property => {
    dispatch(actions.setGalleryProperty(property));
  },
  getPropertyPreview: (slug, successCallback) => {
    dispatch(actions.getPropertyPreview(slug, successCallback));
  },
  deleteRoom: (id, successCallback) => {
    dispatch(actions.deleteRoom(id, successCallback));
  },
  createRoom: (params, successCallback) => {
    dispatch(actions.createRoom(params, successCallback));
  },
  updateRoom: (params, successCallback) => {
    dispatch(actions.updateRoom(params, successCallback));
  },
  setCopyRoomListing: params => {
    dispatch(actions.setCopyRoomListing(params));
  },
  deleteListing: (id, successCallback) => {
    dispatch(actions.deleteListing(id, successCallback));
  },
  createListing: (params, successCallback) => {
    dispatch(actions.createListing(params, successCallback));
  },
  updateListing: (params, successCallback) => {
    dispatch(actions.updateListing(params, successCallback));
  },
  bulkUpdateListings: (params, successCallback) => {
    dispatch(actions.bulkUpdateListings(params, successCallback));
  },
  requestPublish: (propertyId, successCallback, validateErrCallback, inativeErrorCallback) => {
    dispatch(
      pendingApprovalActions.requestPublish(
        propertyId,
        successCallback,
        validateErrCallback,
        inativeErrorCallback,
      ),
    );
  },
});

const categoryMapping = {
  [propertySections.PROPERTY_DETAILS]: propertyDraftCategory.DETAIL,
  [propertySections.GALLERY_MANAGEMENT]: propertyDraftCategory.GALLERY,
};

const statusFilterMapping = {
  [propertyDraftStatus.PENDING]: 'pending_approval',
  [propertyDraftStatus.REJECTED]: 'rejected',
  [propertyDraftStatus.APPROVED]: 'published',
};

@connect(mapStateToProps, mapDispatchToProps)
@withRouter
@withTranslation()
@authControl(platformEntity.PROPERTIES_PROPERTIES, entityAction.READ, props =>
  generatePath('property.homepage', { propertySlug: props.match.params.propertySlug }),
)
export default class PropertyListingManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isChanged: false,
      currentSection: this.initSection(),
      isUpdateGallery: false,
      isSendApprovalSuccess: false,
      unitTypeFacilities: null,
    };
  }

  componentDidMount() {
    this.props.getPropertyDetail(decodeURIComponent(this.props.match.params.propertySlug), () => {
      if (isEmptyObject(this.props.property)) {
        this.props.history.push(generatePath('properties', {}));
      }

      trackViewPropertyManagePage(this.props.property);
    });

    this.props.getPropertyUnitTypeFacilities(res => {
      if (res?.facilities) {
        this.setState({
          unitTypeFacilities: res?.facilities?.map(facility => ({
            ...facility,
            checked: false,
          })),
        });
      }
    });

    window.onbeforeunload = () => {
      if (this.state.isChanged || this.state.isUpdateGallery) {
        return true;
      }
      return null;
    };

    fireCustomEvent('triggerCloseMenu');
  }

  initSection = () => {
    const { history } = this.props;
    const query = qs.parse(history.location?.search);

    if (history?.location?.state?.draftsCategory === 'GALLERY') {
      return propertySections.GALLERY_MANAGEMENT;
    } else if (query?.section === 'PAYMENT_METHOD') {
      return 'PAYMENT_METHOD';
    }

    return propertySections.PROPERTY_DETAILS;
  };

  initStatusFilter = () => {
    const { history } = this.props;
    if (
      history &&
      history.location &&
      history.location.state &&
      history.location.state.draftsStatus
    ) {
      return statusFilterMapping[history.location.state.draftsStatus];
    }

    return 'all_content';
  };

  handleSetChanged = (isChanged = true) => {
    this.setState({
      isChanged,
    });
  };

  handleSetUpdateGallery = (isUpdateGallery = true) => {
    this.setState({
      isUpdateGallery,
    });
  };

  handleSetSection = section => {
    const { t } = this.props;
    if (this.state.isChanged) {
      Modal.confirm({
        title: t('cms:cms.properties.edit.leave_alert.title'),
        content: t('cms:cms.property.listing_management.leave_alert.content'),
        cancelText: t('cms:cms.form.pop_confirm.no'),
        okText: t('cms:cms.form.pop_confirm.yes'),
        onOk: () => {
          this.setState({
            currentSection: section,
            isChanged: false,
          });
        },
      });
    } else {
      this.setState({
        currentSection: section,
      });
    }
  };

  handleSetNextSection = updateParams => {
    const sections = showNavigationLabels(this.props.property);

    const currentSectionIndex = sections.indexOf(this.state.currentSection);
    if (
      this.state.currentSection === propertySections.PAYMENT_PLAN &&
      !this.props.property?.paymentPlan?.paymentOnline
    ) {
      this.setState({
        isChanged: false,
        currentSection: 'PAYMENT_METHOD',
      });
    } else {
      this.setState({
        isChanged: false,
        currentSection:
          currentSectionIndex === sections.length - 1
            ? this.state.currentSection
            : sections[currentSectionIndex + 1],
      });
    }

    this.props.setProperty({
      property: { ...this.props.property, ...updateParams },
    });
    this.props.setGalleryProperty({ property: updateParams });
    if (updateParams.slug) {
      this.props.history.push(generatePath('property.edit', { propertySlug: updateParams.slug }));
    }
  };

  handlePreview = callback => {
    this.props.getPropertyPreview(decodeURIComponent(this.props.match.params.propertySlug), res => {
      callback(res.property);
    });
  };

  handleRequestPublish = () => {
    if (this.state.isChanged) {
      if (this.propertyDetail) {
        this.propertyDetail.handleSubmit().then(res => {
          this.setState({
            isChanged: false,
          });
          if (res.isUpdated) {
            this.executeRequestPublish();
          }
        });
      }
      return;
    }

    if (this.state.isUpdateGallery) {
      if (this.galleryManagement) {
        this.galleryManagement.handleSubmit().then(res => {
          this.setState({
            isUpdateGallery: false,
          });
          if (res.isUpdated) {
            this.executeRequestPublish();
          }
        });
      }
      return;
    }

    this.executeRequestPublish();
  };

  executeRequestPublish = () => {
    this.props.requestPublish(
      this.props.property.id,
      () => {
        this.setState({
          isSendApprovalSuccess: true,
        });
      },
      code => this.landlordTipsModal(code),
      this.noRentPaymentWarningModal,
    );
  };

  landlordTipsModal = (code = '') => {
    const { property, t } = this.props;
    const isNotBoundPayout = code === 'INVALID_PROPERTY_PAYOUT_METHOD';
    if (
      property.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC ||
      property.bookingJourney === bookingJourneyType.INSTANT_BOOK
    ) {
      const modal = Modal.confirm({
        className: 'property-listing-management__landlord-tips-modal',
        title: (
          <div>
            <CloseOutlined
              onClick={() => modal.destroy()}
              className="property-listing-management__landlord-tips-modal__close"
            />
            {isNotBoundPayout
              ? t('cms:cms.property.listing_management.no_payouts_method.tips')
              : t('cms:property_listing_management_nopayment_tip')}
          </div>
        ),
        icon: <ExclamationCircleFilled />,
        okText: t('cms:cms.property.listing_management.btn.to_set_up'),
        cancelText: t('cms:cms.property.listing_management.btn.got_it'),
        width: 380,
        onOk: () => {
          const url = isNotBoundPayout
            ? generatePath('withdrawal')
            : generatePath('paymentList', {});
          window.open(url, '_blank');
        },
      });
    }
  };
  noRentPaymentWarningModal = () => {
    const { t } = this.props;
    Modal.confirm({
      title: t('cms:property_listing_management_inactive_payment'),
      icon: <ExclamationCircleFilled />,
      okText: t('cms:cms.property.listing_management.btn.to_set_up'),
      cancelText: t('cms:cms.property.listing_management.btn.got_it'),
      width: 380,
      onOk: () => {
        const url = generatePath('paymentList', {});
        window.open(url, '_blank');
      },
    });
  };

  handleGotoRoomConfig = () => {
    this.handleSetSection(propertySections.ROOM_CONFIG);
  };

  handleClearUnconfirmedListings = () => {
    this.props.setCopyRoomListing([]);
  };

  handleSendApprovalDetailSuccess = updateParams => {
    this.props.setProperty({
      property: updateParams,
    });

    this.setState({
      isChanged: false,
      isSendApprovalSuccess: true,
    });
  };

  handleSendApprovalGallerySuccess = () => {
    this.setState({
      isUpdateGallery: false,
      isSendApprovalSuccess: true,
    });
  };

  isPublished = () => this.props.property.status === propertyStatus.PUBLISHED;

  handleContinueEditing = () => {
    this.props.getPropertyDetail(decodeURIComponent(this.props.match.params.propertySlug), () => {
      this.setState({
        isChanged: false,
        isSendApprovalSuccess: false,
      });
    });
  };

  handleBackToHomeList = () => {
    const { property } = this.props;
    this.setState(
      {
        isChanged: false,
      },
      () => {
        this.props.history.push(generatePath('property.homepage', { propertySlug: property.slug }));
      },
    );
  };

  getPropertyDraft = () => {
    const { property } = this.props;
    if (property && property.drafts && property.drafts.edges.length > 0) {
      const propertyDrafts = property.drafts.edges.map(draft => draft.node);
      const { EXPIRED, REJECTED, PENDING } = propertyDraftStatus;
      const expiredDraft = propertyDrafts.find(draft => draft.status === EXPIRED);
      const rejectedDraft = propertyDrafts.find(draft => draft.status === REJECTED);
      const pendingDraft = propertyDrafts.find(draft => draft.status === PENDING);

      return expiredDraft || rejectedDraft || pendingDraft;
    }

    return null;
  };

  getCategoryDraft = currentSection => {
    const { property } = this.props;

    if (!property || !property.drafts || property.drafts.edges.length === 0) {
      return null;
    }

    const propertyDrafts = property.drafts.edges.map(draft => draft.node);

    if (currentSection === propertySections.PROPERTY_DETAILS) {
      let expiredDetailDraft = null;
      let rejectedDetailDraft = null;
      let pendingDetailDraft = null;

      propertyDrafts.map(draft => {
        const { status, category } = draft;
        if (
          !expiredDetailDraft &&
          status === propertyDraftStatus.EXPIRED &&
          category === propertyDraftCategory.DETAIL
        ) {
          expiredDetailDraft = draft;
        }
        if (
          !rejectedDetailDraft &&
          status === propertyDraftStatus.REJECTED &&
          category === propertyDraftCategory.DETAIL
        ) {
          rejectedDetailDraft = draft;
        }
        if (
          !pendingDetailDraft &&
          status === propertyDraftStatus.PENDING &&
          category === propertyDraftCategory.DETAIL
        ) {
          pendingDetailDraft = draft;
        }

        return true;
      });

      return expiredDetailDraft || rejectedDetailDraft || pendingDetailDraft;
    }

    if (currentSection === propertySections.GALLERY_MANAGEMENT) {
      let expiredGalleryDraft = null;
      let rejectedGalleryDraft = null;
      let pendingGalleryDraft = null;

      propertyDrafts.map(draft => {
        const { status, category } = draft;
        if (
          !expiredGalleryDraft &&
          status === propertyDraftStatus.EXPIRED &&
          category === propertyDraftCategory.GALLERY
        ) {
          expiredGalleryDraft = draft;
        }
        if (
          !rejectedGalleryDraft &&
          status === propertyDraftStatus.REJECTED &&
          category === propertyDraftCategory.GALLERY
        ) {
          rejectedGalleryDraft = draft;
        }
        if (
          !pendingGalleryDraft &&
          status === propertyDraftStatus.PENDING &&
          category === propertyDraftCategory.GALLERY
        ) {
          pendingGalleryDraft = draft;
        }

        return true;
      });

      return expiredGalleryDraft || rejectedGalleryDraft || pendingGalleryDraft;
    }

    return null;
  };

  render() {
    const { t, property, communication, copiedRoomListings, galleryProperty, allowNoPayment } =
      this.props;
    const { currentSection, isSendApprovalSuccess } = this.state;
    const categoryDraft = this.getCategoryDraft(this.state.currentSection);

    const hasWarningListingRooms = property?.unitTypes?.edges
      ?.map(room => {
        const warningListings = room.node.listings.filter(listing => {
          if (property.bookingJourney === bookingJourneyType.INSTANT_BOOK) {
            return !listing.inventoryCount && listing.inventoryCount !== 0;
          } else {
            return !listing.availability;
          }
        });

        return {
          id: room.node.id,
          warningListings,
        };
      })
      .filter(item => item.warningListings?.length);

    return (
      <div className="property-listing-management">
        <LeaveAlert
          history={this.props.history}
          t={t}
          when={this.state.isChanged || copiedRoomListings.length > 0}
          onConfirmLeavePage={this.handleClearUnconfirmedListings}
        />
        <Choose>
          <When
            condition={communication.getDetail.status !== communicationStatus.IDLE || !property}
          >
            <div className="property-edit property-listing-management__loading">
              <Spin />
            </div>
          </When>
          <When condition={isSendApprovalSuccess}>
            <SubmitSuccess
              t={t}
              isPublished={this.isPublished()}
              section={currentSection}
              onContinueEditing={this.handleContinueEditing}
              onBackToHomeList={this.handleBackToHomeList}
            />
          </When>
          <Otherwise>
            <Header t={t} property={property} type="listing-management" />
            <If condition={this.getPropertyDraft() && !this.isPublished()}>
              <div className="property-listing-management__property-reminder">
                <Reminder
                  t={t}
                  draft={this.getPropertyDraft()}
                  propertySlug={property && property.slug}
                />
              </div>
            </If>

            <If condition={property}>
              <div className="property-listing-management__pending-tip">
                <PendingAlertTips
                  property={property}
                  history={this.props.history}
                  setCurrentSection={section => this.setState({ currentSection: section })}
                />
              </div>
            </If>
            <div className="property-listing-management__main-wrap">
              <NavigationBar
                t={t}
                property={property}
                currentSection={currentSection}
                handleSetSection={this.handleSetSection}
                publishStatus={communication.publish.status}
                onPreview={this.handlePreview}
                onRequestPublish={this.handleRequestPublish}
                isHaveUnconfirmedListing={
                  this.props.copiedRoomListings?.length > 0 || hasWarningListingRooms?.length > 0
                }
                history={this.props.history}
                onClearUnconfirmedListings={this.handleClearUnconfirmedListings}
                onCategoryDraft={this.getCategoryDraft}
              />
              <div className="property-listing-management__right">
                <If condition={categoryDraft && this.isPublished()}>
                  <div className="property-listing-management__part-reminder">
                    <Reminder
                      t={t}
                      draft={categoryDraft}
                      propertySlug={property && property.slug}
                      category={categoryMapping[this.state.currentSection]}
                      isPublished
                    />
                  </div>
                </If>

                <Choose>
                  <When condition={currentSection === propertySections.PROPERTY_DETAILS}>
                    <PropertyDetail
                      t={t}
                      property={property}
                      onChange={this.handleSetChanged}
                      afterSave={
                        this.isPublished()
                          ? this.handleSendApprovalDetailSuccess
                          : this.handleSetNextSection
                      }
                      onRef={node => {
                        this.propertyDetail = node;
                      }}
                      draft={this.getPropertyDraft() || categoryDraft}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PROPERTY_ADDRESS}>
                    <AddressDetail
                      t={t}
                      property={property}
                      onChange={this.handleSetChanged}
                      afterSave={this.handleSetNextSection}
                      onRef={node => {
                        this.addressDetail = node;
                      }}
                      draft={this.getPropertyDraft()}
                    />
                  </When>
                  <When condition={currentSection === propertySections.ACCEPTANCE_CRITERIA}>
                    <AcceptanceCriteria
                      t={t}
                      property={property}
                      afterSave={this.handleSetNextSection}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PROPERTY_FACILITY}>
                    <FacilityDetail
                      t={t}
                      property={property}
                      onChange={this.handleSetChanged}
                      afterSave={this.handleSetNextSection}
                      onRef={node => {
                        this.facilityDetail = node;
                      }}
                      draft={this.getPropertyDraft()}
                    />
                  </When>
                  <When condition={currentSection === propertySections.ROOM_CONFIG}>
                    <RoomDetail
                      t={t}
                      property={property}
                      onChange={this.handleSetChanged}
                      deleteRoom={this.props.deleteRoom}
                      createRoom={this.props.createRoom}
                      updateRoom={this.props.updateRoom}
                      setProperty={this.props.setProperty}
                      setCopyRoomListing={this.props.setCopyRoomListing}
                      copiedRoomListings={this.props.copiedRoomListings}
                      communication={communication}
                      unitTypeFacilities={this.state.unitTypeFacilities}
                      setGalleryProperty={this.props.setGalleryProperty}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PRICE_AND_AVAILABILITY}>
                    <ListingDetail
                      t={t}
                      property={property}
                      onChange={this.handleSetChanged}
                      hasWarningListingRooms={hasWarningListingRooms}
                      copiedRoomListings={this.props.copiedRoomListings}
                      deleteListing={this.props.deleteListing}
                      createListing={this.props.createListing}
                      updateListing={this.props.updateListing}
                      refetch={this.handleContinueEditing}
                      bulkUpdateListings={this.props.bulkUpdateListings}
                      setCopyRoomListing={this.props.setCopyRoomListing}
                      setProperty={this.props.setProperty}
                      setGalleryProperty={this.props.setGalleryProperty}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PRICE_CALC_DISPLAY}>
                    <PriceCalcDisplay
                      t={t}
                      property={property}
                      setProperty={this.props.setProperty}
                      afterSave={this.handleSetNextSection}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PAYMENT_PLAN}>
                    <PaymentPlan
                      t={t}
                      property={property}
                      setProperty={this.props.setProperty}
                      afterSave={this.handleSetNextSection}
                      allowNoPayment={allowNoPayment}
                    />
                  </When>
                  <When
                    condition={
                      currentSection === 'PAYMENT_METHOD' && !property?.paymentPlan?.paymentOnline
                    }
                  >
                    <PaymentMethod
                      t={t}
                      property={property}
                      setProperty={this.props.setProperty}
                      afterSave={this.handleSetNextSection}
                    />
                  </When>
                </Choose>
                <GalleryManagement
                  t={t}
                  property={galleryProperty}
                  onRef={node => {
                    this.galleryManagement = node;
                  }}
                  onGotoRoomConfig={this.handleGotoRoomConfig}
                  setGalleryProperty={this.props.setGalleryProperty}
                  onSetUpdateGallery={this.handleSetUpdateGallery}
                  isHidden={currentSection !== propertySections.GALLERY_MANAGEMENT}
                  onSendApprovalGallerySuccess={this.handleSendApprovalGallerySuccess}
                  statusFilter={this.initStatusFilter()}
                  setProperty={this.props.setProperty}
                  draft={this.getPropertyDraft() && categoryDraft}
                />
              </div>
            </div>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

PropertyListingManagement.propTypes = {
  t: PropTypes.func.isRequired,
  history: PropTypes.object,
  getPropertyDetail: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      propertySlug: PropTypes.string,
    }).isRequired,
  }).isRequired,
  property: PropTypes.object,
  setProperty: PropTypes.func.isRequired,
  communication: PropTypes.object,
  getPropertyPreview: PropTypes.func.isRequired,
  deleteRoom: PropTypes.func.isRequired,
  createRoom: PropTypes.func,
  updateRoom: PropTypes.func,
  setCopyRoomListing: PropTypes.func,
  copiedRoomListings: PropTypes.array,
  deleteListing: PropTypes.func,
  createListing: PropTypes.func,
  updateListing: PropTypes.func,
  bulkUpdateListings: PropTypes.func,
  requestPublish: PropTypes.func,
  galleryProperty: PropTypes.object,
  setGalleryProperty: PropTypes.func,
};

PropertyListingManagement.defaultProps = {
  t: () => {},
  history: {},
  getPropertyDetail: () => {},
  match: {
    params: {
      propertySug: '',
    },
  },
  property: {},
  setProperty: () => {},
  communication: {},
  getPropertyPreview: () => {},
  deleteRoom: () => {},
  copiedRoomListings: [],
  setCopyRoomListing: () => {},
  createRoom: () => {},
  updateRoom: () => {},
  deleteListing: () => {},
  createListing: () => {},
  updateListing: () => {},
  bulkUpdateListings: () => {},
  requestPublish: () => {},
  galleryProperty: {},
  setGalleryProperty: () => {},
};
